// ═══════════════════════════════════════════════════════════════
// permisos.js — Motor de evaluación de permisos v2
// ═══════════════════════════════════════════════════════════════
// Reglas del modelo (ver docs/MODELO_ORGANIZACIONAL.md):
//
//   USUARIO       → identidad + rolId (uno solo) + rolPlataformaId? (opcional).
//   ROL           → catálogo /roles con permisos[{modulo, accion, scope, filtros?}]
//                   + extiende? para cascada vertical (Operaciones→Supervisor→...→Operador).
//   ASIGNACIÓN    → /asignaciones {userId, scope:{tipo, empresaId|sistemaTotal|wizard|...},
//                                   desde, hasta?, esActivo}. Define alcance de empresas.
//   WIZARD        → flag en /asignaciones (no en /usuarios). Saltea TODO check.
//
// Funciones principales (todas async porque consultan Firestore con cache):
//
//   await puedeHacer(user, modulo, accion, recurso?, ctx?)
//     → boolean. Evalúa permiso completo: scope + filtros + alcance.
//
//   await getEmpresasAlcance(user, ctx?)
//     → string[] | '*'. Empresas que ve el usuario. '*' = todas (sistemaTotal).
//
//   await getModulosVisibles(user, ctx?)
//     → string[]. Módulos donde tiene al menos acción 'ver'.
//
//   await esWizard(user, ctx?)
//     → boolean. Atajo: si true, todas las funciones retornan true/'*' inmediatamente.
//
//   await permisosEfectivos(user, ctx?)
//     → array de permisos resueltos con cascada (extiende). Para debug/UI.
//
// CTX (cache compartido para evitar roundtrips entre llamadas):
//   { rolCache?: Map, asignacionesCache?: Map, modulosCache?: Map, wizardCache?: Map }
//
// IMPORTANTE: este archivo es CLIENT-SIDE. La versión server-side (Admin SDK)
// vive en `functions/permisos.js` y tiene la misma API pero usa firestore-admin.
// ═══════════════════════════════════════════════════════════════

import {
  collection, getDocs, doc, getDoc, query, where, limit as fbLimit,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS, USER_TIPOS } from './constants'
import { esAccionValida, SCOPES_VALIDOS } from './accionesPermiso'
import { recursoPasaFiltros } from './filtrosPermiso'
import { empresasHeredadasPorEquipo } from './alcanceEquipo'

// Orden de "amplitud" — útil para sort de permisos (más amplio primero).
const SCOPE_ORDER = { todo: 5, asignado: 4, involucrado: 3, compartido: 3, propio: 2 }

// Tope de profundidad de cascada para evitar loops infinitos en `extiende`.
const MAX_PROFUNDIDAD_CASCADA = 8

// ─────────────────────────────────────────────────────────────────
// PUEDE HACER — función principal
// ─────────────────────────────────────────────────────────────────

/**
 * Determina si un usuario puede ejecutar una acción sobre un recurso.
 *
 * @param {Object} user       — userDoc con { id, rolId, rolPlataformaId? }
 * @param {string} modulo     — código del módulo (ej. 'casos')
 * @param {string} accion     — código de la acción (ej. 'cerrar')
 * @param {Object} [recurso]  — doc del recurso { id, empresaId?, creadoPor?, asignadoA?, participantesUserIds?, ... }
 * @param {Object} [ctx]      — cache compartido { rolCache, asignacionesCache, ... }
 * @returns {Promise<boolean>}
 */
export async function puedeHacer(user, modulo, accion, recurso = null, ctx = {}) {
  if (!user) return false
  if (!modulo || !accion) return false

  // ─── Atajo Wizard (saltea todo) ──────────────────────────────
  if (await esWizard(user, ctx)) return true

  // Validar que la acción exista en el catálogo (defensa contra typos).
  if (!esAccionValida(modulo, accion)) {
    console.warn(`[permisos] acción desconocida: ${modulo}.${accion}`)
    // No retornamos false sin más — quizá el rol declara permisos para módulos
    // dinámicos no listados en MODULOS_CATALOGO. Seguimos evaluando.
  }

  // ─── Resolver rol con cascada (extiende) ─────────────────────
  const permisos = await permisosEfectivos(user, ctx)
  if (!permisos || permisos.length === 0) return false

  // Filtrar matches (modulo, accion)
  const matches = permisos.filter(p =>
    p.modulo === modulo && p.accion === accion
  )
  if (matches.length === 0) return false

  // ─── Validar alcance de empresa (si el recurso tiene empresaId) ─
  if (recurso && recurso.empresaId) {
    const alcance = await getEmpresasAlcance(user, ctx)
    if (alcance !== '*' && !alcance.includes(recurso.empresaId)) {
      return false
    }
  }

  // ─── Evaluar cada permiso match (OR entre permisos) ──────────
  for (const p of matches) {
    // 1) Evaluar scope.
    const scopeOk = evaluarScope(user, p.scope, recurso, ctx)
    // Algunos scopes son async (asignado para listar empresas, etc.)
    const scopeOkResolved = scopeOk instanceof Promise ? await scopeOk : scopeOk
    if (!scopeOkResolved) continue

    // 2) Evaluar filtros declarativos (si hay y hay recurso).
    if (p.filtros && recurso) {
      if (!recursoPasaFiltros(modulo, recurso, p.filtros)) continue
    }
    // 3) Si el recurso es null y hay filtros declarados, no podemos evaluarlos
    //    pero permitimos pasar (caso típico: "puede crear caso" — todavía no hay recurso).

    return true  // este permiso matcheó completo
  }
  return false
}

/**
 * Evalúa si un scope se cumple para un usuario y un recurso.
 * Algunos scopes son síncronos, otros consultan asignaciones (async).
 */
function evaluarScope(user, scope, recurso, ctx) {
  if (!scope || scope === 'todo') return true

  // Sin recurso → solo 'todo' aplica; los demás scopes necesitan un recurso concreto.
  if (!recurso) {
    return false
  }

  const userId = user.id // el id del DOCUMENTO: `user` es el userDoc (docConId), nunca su campo `uid`
  if (!userId) return false

  if (scope === 'propio') {
    return recurso.creadoPor === userId
        || recurso.ownerUserId === userId
        || recurso.owner === userId
  }

  if (scope === 'asignado') {
    return recurso.asignadoA === userId
        || recurso.responsableUserId === userId
  }

  if (scope === 'involucrado' || scope === 'compartido') {
    const arr = recurso.participantesUserIds
      || recurso.participantes
      || recurso.visibleParaUserIds
      || []
    return Array.isArray(arr) && arr.includes(userId)
  }

  return false  // scope desconocido → deny por default
}

// ─────────────────────────────────────────────────────────────────
// ALCANCE DE EMPRESAS
// ─────────────────────────────────────────────────────────────────

/**
 * Devuelve la lista de empresaIds que el usuario alcanza, o '*' si tiene
 * scope sistema (Operaciones, Wizard).
 *
 * Combina DOS capas:
 *   1. Alcance propio — sus asignaciones:
 *      - scope.tipo='sistema' con sistemaTotal=true   → '*'
 *      - scope.tipo='empresa' con empresaId           → [empresaId]
 *      - scope.tipo='grupo' con grupoId               → [...empresaIds del grupo]
 *      - scope.tipo='supervision' (legacy)            → unión de empresas de cada consultor
 *   2. Alcance heredado del equipo — `user.empresasEquipo[]` (campo derivado
 *      del organigrama / reportaA, mantenido por el trigger): un jefe ve las
 *      empresas de quienes le reportan, directa o indirectamente.
 *
 * @returns {Promise<string[] | '*'>}
 */
export async function getEmpresasAlcance(user, ctx = {}) {
  if (!user) return []
  if (await esWizard(user, ctx)) return '*'

  const empresas = new Set()

  // ── Capa 1 — alcance propio (asignaciones del usuario) ──
  const asignaciones = await listarAsignacionesActivas(user, ctx)
  for (const a of (asignaciones || [])) {
    const s = a.scope || {}
    if (s.sistemaTotal === true) return '*'
    if (s.tipo === 'empresa' && s.empresaId) empresas.add(s.empresaId)
    if (s.tipo === 'grupo' && s.grupoId) {
      const grupo = await getGrupoCached(s.grupoId, ctx)
      if (grupo?.empresaIds) grupo.empresaIds.forEach(e => empresas.add(e))
    }
    if (s.tipo === 'supervision' && Array.isArray(s.consultoresIds)) {
      // Legacy — el organigrama (empresasEquipo) lo reemplaza. Se retira en B9.
      for (const consId of s.consultoresIds) {
        const empresasCons = await getEmpresasAlcanceDeUserId(consId, ctx)
        if (empresasCons === '*') return '*'
        empresasCons.forEach(e => empresas.add(e))
      }
    }
  }

  // ── Capa 2 — alcance heredado del equipo (organigrama / reportaA) ──
  // empresasEquipo[] es un campo derivado que mantiene el trigger
  // recalcularDerivadosEquipoGlobal: unión de las empresas de todo el equipo.
  // El staff lo hereda entero; un no-staff, sólo lo que ya es suyo — la misma
  // regla que el servidor (./alcanceEquipo.js, espejo con paridad).
  empresasHeredadasPorEquipo({
    esStaff: esStaff(user),
    empresasEquipo: user.empresasEquipo,
    empresaIds: user.empresaIds,
  }).forEach(e => empresas.add(e))

  return Array.from(empresas)
}

/**
 * Variante por userId (interna, para evitar cargar el userDoc de subordinados).
 * Las asignaciones se consultan directamente.
 */
async function getEmpresasAlcanceDeUserId(userId, ctx = {}) {
  if (!userId) return []
  if (ctx.empresasAlcanceCache?.[userId]) return ctx.empresasAlcanceCache[userId]

  const q = query(
    collection(db, COLLECTIONS.ASIGNACIONES),
    where('userId', '==', userId),
    where('esActivo', '==', true),
  )
  const snap = await getDocs(q)
  const docs = snap.docs.map(docConId)

  const empresas = new Set()
  for (const a of docs) {
    const s = a.scope || {}
    if (s.sistemaTotal === true) {
      const result = '*'
      if (ctx.empresasAlcanceCache) ctx.empresasAlcanceCache[userId] = result
      return result
    }
    if (s.tipo === 'empresa' && s.empresaId) empresas.add(s.empresaId)
    if (s.tipo === 'grupo' && s.grupoId) {
      const grupo = await getGrupoCached(s.grupoId, ctx)
      if (grupo?.empresaIds) grupo.empresaIds.forEach(e => empresas.add(e))
    }
    // No recursión más profunda — un supervisor que supervisa supervisores debería ser raro
    // y se modela como Operaciones.
  }
  const result = Array.from(empresas)
  if (ctx.empresasAlcanceCache) ctx.empresasAlcanceCache[userId] = result
  return result
}

// ─────────────────────────────────────────────────────────────────
// MODULOS VISIBLES (para sidebar)
// ─────────────────────────────────────────────────────────────────

/**
 * Devuelve la lista de módulos donde el usuario tiene al menos accion='ver'.
 * Útil para construir el sidebar dinámicamente.
 *
 * @returns {Promise<string[]>}
 */
export async function getModulosVisibles(user, ctx = {}) {
  if (!user) return []
  if (await esWizard(user, ctx)) {
    // Wizard ve todos los módulos del catálogo + plataforma.
    const { listarModulos } = await import('./accionesPermiso')
    return listarModulos()
  }
  const permisos = await permisosEfectivos(user, ctx)
  const set = new Set()
  for (const p of permisos) {
    if (p.accion === 'ver') set.add(p.modulo)
  }
  return Array.from(set)
}

// ─────────────────────────────────────────────────────────────────
// WIZARD (atajo de root)
// ─────────────────────────────────────────────────────────────────

/**
 * Determina si el usuario tiene una asignación activa con scope.wizard=true.
 * Cacheable.
 */
export async function esWizard(user, ctx = {}) {
  if (!user) return false
  const userId = user.id // el id del DOCUMENTO: `user` es el userDoc (docConId), nunca su campo `uid`
  if (!userId) return false

  if (ctx.wizardCache?.[userId] != null) return ctx.wizardCache[userId]

  // Atajo: si el doc del user ya tiene esWizard (campo derivado), lo usamos.
  if (user.esWizard === true) {
    if (ctx.wizardCache) ctx.wizardCache[userId] = true
    return true
  }

  // Caso normal: consultar /asignaciones del usuario.
  const q = query(
    collection(db, COLLECTIONS.ASIGNACIONES),
    where('userId', '==', userId),
    where('esActivo', '==', true),
    fbLimit(10),  // un wizard típicamente tiene 1-2 asignaciones; tope para safety.
  )
  const snap = await getDocs(q)
  const hasWizard = snap.docs.some(d => d.data()?.scope?.wizard === true)
  if (ctx.wizardCache) ctx.wizardCache[userId] = hasWizard
  return hasWizard
}

// ─────────────────────────────────────────────────────────────────
// STAFF MILBRANDS — flag, NO email (fuente única del criterio en frontend)
// ─────────────────────────────────────────────────────────────────

/**
 * ¿Este userDoc es staff de Milbrands? Por el FLAG canónico (`tipo:'milbrands'`
 * o wizard), NUNCA por el dominio del email. Espejo cliente de `esStaffServer`
 * del backend (C.1 / ADR 003): la autoridad de staff es el flag, no el mail.
 * PURO y síncrono — lee solo campos ya presentes en el
 * userDoc (sin Firestore). El wizard por asignación-scope sin flag denormalizado
 * necesita `esWizard()` async; acá cubrimos el caso denormalizado (`esWizard`) +
 * `rolId:'WIZARD'`, suficiente para gating de login/UI (el backend es la frontera
 * real de autorización).
 *
 * @param {Object} userDoc — doc de /usuarios
 * @returns {boolean}
 */
export function esStaff(userDoc) {
  if (!userDoc) return false
  return userDoc.tipo === USER_TIPOS.MILBRANDS
    || userDoc.esWizard === true
    || userDoc.rolId === 'WIZARD'
}

/**
 * ¿Puede este usuario "Ver como" (impersonar) a otro? SÓLO el Wizard — decisión
 * de Mariano (8-sep-2026): "wizard es wizard, ve todo; el que no es wizard no
 * ve wizard". Una sola fuente para el efecto de AuthContext y el botón
 * VerComoButton: antes cada uno tenía su propio criterio y divergían (el botón
 * se mostraba a todo staff y el efecto lo ignoraba). Es 100 % cliente: no hay
 * gate server de impersonación — los requests salen con el uid real.
 * @param {Object} userDoc — doc de /usuarios del usuario REAL
 * @returns {boolean}
 */
export function puedeImpersonar(userDoc) {
  return !!userDoc && userDoc.esWizard === true
}

// ─────────────────────────────────────────────────────────────────
// PERMISOS EFECTIVOS (con cascada `extiende`)
// ─────────────────────────────────────────────────────────────────

/**
 * Devuelve la lista plana de permisos del usuario, mergeando con el rol padre
 * vía `extiende` (recursivo hasta MAX_PROFUNDIDAD_CASCADA).
 *
 * Si el usuario tiene `rolPlataformaId`, también se mergean esos permisos.
 *
 * @returns {Promise<Array<{modulo, accion, scope, filtros?}>>}
 */
export async function permisosEfectivos(user, ctx = {}) {
  if (!user) return []
  const acc = []
  if (user.rolId) {
    const permisos = await resolverPermisosRol(user.rolId, ctx, 0)
    acc.push(...permisos)
  }
  if (user.rolPlataformaId) {
    const permisos = await resolverPermisosRol(user.rolPlataformaId, ctx, 0)
    acc.push(...permisos)
  }
  return acc
}

/**
 * ¿El rol está activo? Hay dos campos para apagar un rol: `esActivo` (el del
 * seed) y `activo` (el que escribe el ABM de roles, `RolesPage.jsx`). Un rol
 * apagado por cualquiera de los dos no da permisos acá, no los da en el
 * servidor (`functions/permisos.js`, `_resolverPermisosRol`) y no se ofrece en
 * las pantallas que asignan roles.
 * @param {object|null|undefined} rol
 */
export function rolActivo(rol) {
  return !!rol && rol.esActivo !== false && rol.activo !== false
}

/**
 * Resuelve permisos de un rol siguiendo la cascada `extiende`.
 * Retorna la unión de permisos (rol hijo + rol padre + abuelo...).
 * Si un permiso del hijo matchea (modulo, accion) del padre, el hijo gana (override).
 */
async function resolverPermisosRol(rolId, ctx, profundidad) {
  if (profundidad > MAX_PROFUNDIDAD_CASCADA) {
    console.warn(`[permisos] cascada demasiado profunda rolId=${rolId}`)
    return []
  }
  const rol = await getRolCached(rolId, ctx)
  if (!rolActivo(rol)) return []

  // Normalizar al formato canónico {modulo, accion, scope, filtros?}.
  // El formato legacy {moduloCodigo, funcionCodigo, scope} se mapea automáticamente
  // — la deuda se elimina cuando B5 refactorice RolesPage para escribir formato nuevo.
  const propios = (Array.isArray(rol.permisos) ? rol.permisos : [])
    .map(normalizarPermiso)
    .filter(p => p !== null && p.modulo && p.accion)

  if (rol.extiende) {
    const padre = await resolverPermisosRol(rol.extiende, ctx, profundidad + 1)
    // Merge: el hijo gana sobre el padre cuando coincide (modulo, accion).
    const claveHijo = new Set(propios.map(p => `${p.modulo}.${p.accion}`))
    const filtradoPadre = padre.filter(p => !claveHijo.has(`${p.modulo}.${p.accion}`))
    return [...filtradoPadre, ...propios]
  }
  return propios
}

/**
 * Normaliza una entrada de permiso al formato canónico {modulo, accion, scope, filtros?}.
 * Acepta el formato legacy {moduloCodigo, funcionCodigo, scope}.
 *
 * Una vez que B5 refactorice RolesPage y todos los roles se re-seamen,
 * esta función se simplifica (o se borra y los permisos se asumen ya en formato nuevo).
 */
function normalizarPermiso(p) {
  if (!p || typeof p !== 'object') return null
  return {
    modulo: p.modulo || p.moduloCodigo || null,
    accion: p.accion || p.funcionCodigo || null,
    scope: p.scope || 'todo',
    filtros: p.filtros || null,
  }
}

// ─────────────────────────────────────────────────────────────────
// HELPERS DE CACHE
// ─────────────────────────────────────────────────────────────────

async function getRolCached(rolId, ctx) {
  if (!rolId) return null
  if (ctx.rolCache?.[rolId]) return ctx.rolCache[rolId]

  // Lookup por id directo y por codigo (algunos rolId son `codigo`, no document id)
  let rol = null
  const refDirect = doc(db, COLLECTIONS.ROLES, rolId)
  const snapDirect = await getDoc(refDirect)
  if (snapDirect.exists()) {
    rol = docConId(snapDirect)
  } else {
    // Fallback: buscar por campo `codigo`.
    const q = query(
      collection(db, COLLECTIONS.ROLES),
      where('codigo', '==', rolId),
      fbLimit(1),
    )
    const snap = await getDocs(q)
    if (!snap.empty) {
      const d = snap.docs[0]
      rol = docConId(d)
    }
  }
  if (ctx.rolCache) ctx.rolCache[rolId] = rol
  return rol
}

async function listarAsignacionesActivas(user, ctx) {
  const userId = user.id // el id del DOCUMENTO: `user` es el userDoc (docConId), nunca su campo `uid`
  if (!userId) return []
  if (ctx.asignacionesCache?.[userId]) return ctx.asignacionesCache[userId]

  const q = query(
    collection(db, COLLECTIONS.ASIGNACIONES),
    where('userId', '==', userId),
    where('esActivo', '==', true),
  )
  const snap = await getDocs(q)
  const list = snap.docs.map(docConId)
  if (ctx.asignacionesCache) ctx.asignacionesCache[userId] = list
  return list
}

async function getGrupoCached(grupoId, ctx) {
  if (!grupoId) return null
  if (ctx.grupoCache?.[grupoId]) return ctx.grupoCache[grupoId]
  // Grupos son una colección futura — por ahora retornamos null.
  // Cuando se implemente, agregar COLLECTIONS.GRUPOS y lookup aquí.
  const grupo = null
  if (ctx.grupoCache) ctx.grupoCache[grupoId] = grupo
  return grupo
}

// ─────────────────────────────────────────────────────────────────
// EXPOSICIÓN EN WINDOW PARA DEBUG (consola)
// ─────────────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__puedeHacer = puedeHacer
  window.__getEmpresasAlcance = getEmpresasAlcance
  window.__getModulosVisibles = getModulosVisibles
  window.__esWizard = esWizard
  window.__permisosEfectivos = permisosEfectivos
}

// ─────────────────────────────────────────────────────────────────
// EXPORTS para tests
// ─────────────────────────────────────────────────────────────────

export const _internal = {
  evaluarScope,
  resolverPermisosRol,
  normalizarPermiso,
  SCOPE_ORDER,
  MAX_PROFUNDIDAD_CASCADA,
}
